import { montarRespostaDoPonto, pctTexto, porMil, semDiferenca, type PontoPreco, type ResultadoHibrido, type ZonaPreco } from '@via-tax/contracts'
import { Selo } from '@via-tax/ui'
import { useState } from 'react'
import { reais } from '../formatos'

/**
 * A Resposta do Simples puro × híbrido (ADR 0023): a decisão em uma frase, três números e o preço escolhido
 * dentro da faixa em que os dois lados ganham — do piso, que mantém a sobra de hoje, ao teto, em que o cliente
 * empresa paga o mesmo que pagaria no puro. Tudo sai de `zona`; a tela só interpola entre os pontos do motor,
 * porque a conta é linear no preço.
 *
 * Os textos vêm de `montarRespostaDoPonto` (contracts): tela, relatório técnico e Resumo do cliente dizem a
 * mesma coisa com as mesmas palavras (ADR 0024).
 */

const PASSOS = 1000
/** ponto da faixa na posição t (0 = piso, PASSOS = teto), em centavos inteiros */
function interpolar(a: PontoPreco, b: PontoPreco, t: number): PontoPreco {
  const i = (x: number, y: number) => x + Math.round(((y - x) * t) / PASSOS)
  const preco = i(a.preco, b.preco)
  const tributo = i(a.tributo, b.tributo)
  return { preco, tributo, nota: preco + tributo, custoCliente: preco, sobra: i(a.sobra, b.sobra) }
}

export function RespostaHibrido({ h, z }: { h: ResultadoHibrido; z: ZonaPreco }) {
  const posSugerido = PASSOS / 2
  const [t, setT] = useState(z.existe ? posSugerido : PASSOS)
  const x = z.existe ? interpolar(z.piso, z.teto, t) : z.teto
  const efeitoSem = z.semCredito ? z.semCredito.sobraMesmaNota - z.semCredito.sobraPuro : 0
  const ganho = x.sobra - z.sobraPuro + efeitoSem
  const r = montarRespostaDoPonto(h, x, ganho, t === 0 ? 'piso' : t === PASSOS ? 'teto' : 'sugerido', z)
  const mil = (v: number) => porMil(v, z.receitaHoje)
  const cores = [undefined, r.variacaoCustoCliente <= 0 ? 'text-ok' : 'text-alerta', undefined]

  const linhas: Array<{ rotulo: string; hoje: string; puro: string; hib: string; forte?: boolean }> = [
    { rotulo: 'Valor da nota', hoje: reais(100_000), puro: reais(100_000), hib: reais(r.porMilPonto.nota) },
    { rotulo: 'IBS/CBS destacado na nota', hoje: '—', puro: '—', hib: reais(r.porMilPonto.tributo) },
    {
      rotulo: 'Crédito que o cliente toma',
      hoje: reais(mil(z.receitaHoje - z.custoClienteHoje.lucroReal)),
      puro: reais(mil(z.receitaHoje - z.custoClientePuro)),
      hib: reais(r.porMilPonto.tributo),
    },
    { rotulo: 'Custo real para o cliente', hoje: reais(mil(z.custoClienteHoje.lucroReal)), puro: reais(mil(z.custoClientePuro)), hib: reais(r.porMilPonto.custoCliente), forte: true },
    { rotulo: 'Fica para a empresa', hoje: reais(mil(z.sobraPuro)), puro: reais(mil(z.sobraPuro)), hib: reais(r.porMilPonto.sobra), forte: true },
  ]

  return (
    <section aria-labelledby="resposta-titulo" className="flex flex-col gap-5 rounded-lg border border-linha bg-painel p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-3xl">
          <p className="text-xs text-tinta-3">
            Decisão para {h.ano} · cenário base (CBS {pctTexto(h.aliquotas.cbs)} + IBS {pctTexto(h.aliquotas.ibs)})
          </p>
          <h2 id="resposta-titulo" className="mt-1 text-2xl font-semibold">
            {r.titulo}
          </h2>
          <p className="mt-2 text-sm text-tinta-2">{r.frase}</p>
        </div>
        {h.aliquotas.status === 'ESTIMATIVA' && <Selo tom="neutro">alíquota estimada</Selo>}
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        {r.numeros.map((n, i) => (
          <div key={n.rotulo} className={`flex flex-col gap-1 rounded-md p-4 ${i === 2 ? 'bg-navy text-white' : 'bg-fundo'}`}>
            <span className={`text-xs font-medium ${i === 2 ? 'text-white/75' : 'text-tinta-3'}`}>{n.rotulo}</span>
            <span className={`num text-2xl font-semibold ${i === 2 ? '' : (cores[i] ?? 'text-navy')}`}>{n.valor}</span>
            <span className={`text-xs ${i === 2 ? 'text-white/75' : 'text-tinta-2'}`}>{n.nota}</span>
          </div>
        ))}
      </div>

      {z.existe && (
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-end justify-between gap-2">
            <label htmlFor="preco-faixa" className="text-sm font-semibold">
              Escolha o preço para o cliente empresa
            </label>
            <div className="flex gap-1.5">
              {(
                [
                  ['Piso', 0],
                  ['Sugerido', posSugerido],
                  ['Teto', PASSOS],
                ] as const
              ).map(([rotulo, pos]) => (
                <button
                  key={rotulo}
                  type="button"
                  onClick={() => setT(pos)}
                  aria-pressed={t === pos}
                  className={`rounded-md border px-3 py-1.5 text-xs font-medium ${t === pos ? 'border-navy bg-navy text-white' : 'border-linha text-navy hover:border-navy'}`}
                >
                  {rotulo}
                </button>
              ))}
            </div>
          </div>
          <input id="preco-faixa" type="range" min={0} max={PASSOS} step={5} value={t} onChange={(e) => setT(Number(e.target.value))} className="w-full accent-acao" />
          <div className="flex justify-between gap-3 text-[11px] text-tinta-3" aria-hidden>
            <span>Piso: a empresa fica com o mesmo de hoje</span>
            <span className="text-right">Teto: o cliente paga o mesmo que no puro</span>
          </div>
          <p className="text-xs text-tinta-2">
            Entre o piso e o teto os dois lados ganham: mais perto do piso, o cliente empresa compra mais barato e a empresa ganha em competitividade; mais perto do teto, o ganho
            fica com a empresa.
          </p>
        </div>
      )}

      <div className="flex flex-col gap-1">
        <p className="text-[11px] text-tinta-3 sm:hidden">Arraste a tabela para o lado para ver as três colunas.</p>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[34rem] text-sm">
            <caption className="pb-2 text-left text-sm font-semibold">Uma venda de {reais(100_000)} a uma empresa que se credita</caption>
            <thead>
              <tr className="text-left text-xs text-tinta-3">
                <th scope="col" className="pb-2 font-medium" />
                <th scope="col" className="pb-2 text-right font-medium">
                  Hoje · cliente no Lucro Real
                </th>
                <th scope="col" className="pb-2 text-right font-medium">
                  Puro {h.ano}
                </th>
                <th scope="col" className="bg-acao/10 pb-2 pr-2 text-right font-semibold text-navy">
                  Híbrido {h.ano}
                </th>
              </tr>
            </thead>
            <tbody>
              {linhas.map((l) => (
                <tr key={l.rotulo} className={`border-t border-linha ${l.forte ? 'font-semibold' : ''}`}>
                  <th scope="row" className="py-1.5 text-left font-[inherit]">
                    {l.rotulo}
                  </th>
                  <td className="num py-1.5 text-right text-tinta-2">{l.hoje}</td>
                  <td className="num py-1.5 text-right text-tinta-2">{l.puro}</td>
                  <td className="num bg-acao/5 py-1.5 pr-2 text-right">{l.hib}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex flex-col gap-2 text-sm">
        <p>
          <strong>Cliente do Lucro Real:</strong> hoje ele credita {pctTexto(z.pisCofinsNaoCumulativo)} de PIS/COFINS sobre o que compra da empresa. Em {h.ano} isso acaba em
          qualquer cenário: no puro o crédito cai para o IBS/CBS de dentro do DAS. No preço escolhido, o custo dele fica{' '}
          <strong className={r.variacaoCustoClienteLucroReal > 0 ? 'text-alerta' : 'text-ok'}>
            {semDiferenca(r.variacaoCustoClienteLucroReal)
              ? 'igual ao'
              : `${pctTexto(r.variacaoCustoClienteLucroReal)} ${r.variacaoCustoClienteLucroReal > 0 ? 'acima do' : 'abaixo do'}`}
          </strong>{' '}
          de hoje. Cliente do Presumido não credita nada hoje e passa a creditar o IBS/CBS da nota.
        </p>
        {z.exportacao && (
          <p className="text-tinta-2">
            Vendas ao exterior são imunes ao IBS e à CBS (LC 214, arts. 8º e 79) e o crédito das compras é mantido: {reais(z.exportacao.receitaHoje)} no ano, que{' '}
            {r.efeitoExportacao >= 0 ? 'somam' : 'tiram'} <span className="num">{reais(Math.abs(r.efeitoExportacao))}</span> {r.efeitoExportacao >= 0 ? 'ao' : 'do'} resultado — o
            preço delas não muda.
          </p>
        )}
        {z.semCredito && (
          <p className="text-tinta-2">
            Consumidor final e empresas sem crédito seguem pagando a mesma nota de hoje: essas vendas {r.efeitoSemCredito >= 0 ? 'somam' : 'tiram'}{' '}
            <span className="num">{reais(Math.abs(r.efeitoSemCredito))}</span> {r.efeitoSemCredito >= 0 ? 'ao' : 'do'} resultado do ano, já considerado acima.
          </p>
        )}
      </div>
    </section>
  )
}
